import axios from "axios";
import { COIN_SYMBOL_IDS } from "../../../SymbolData";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { WILD_SYMBOL_ID } from "../../../SymbolData";
import StorageManager from "../../../utils/StorageManager";
import { PROVIDER_CONFIG } from "./providerConfig";

/* -------------------------------------------------------
   HELPER: Normalize Screen Data
   Converts string symbol IDs to numbers
------------------------------------------------------- */
function normalizeScreenData(screenData) {
  if (!screenData || !Array.isArray(screenData)) return screenData;
  const map = PROVIDER_CONFIG.SYMBOL_MAP;
  return screenData.map((reel) => {
    if (!Array.isArray(reel)) return reel;
    return reel.map((symbolId) => {
      const n = typeof symbolId === "string" ? parseInt(symbolId, 10) : symbolId;
      return map[n] !== undefined ? map[n] : n;
    });
  });
}



/* -------------------------------------------------------
   HELPER: Check if screen has regular symbols (non-coin, non-blank)
------------------------------------------------------- */
function hasRegularSymbols(screenData) {
  if (!screenData || !Array.isArray(screenData)) return false;
  for (const reel of screenData) {
    if (!Array.isArray(reel)) continue;
    for (const symbolId of reel) {
      const n = typeof symbolId === "string" ? parseInt(symbolId, 10) : symbolId;
      if (n !== 0 && !COIN_SYMBOL_IDS.includes(n)) return true;
    }
  }
  return false;
}

/* -------------------------------------------------------
   HELPER: Transform screen for Hold and Win rendering
   Replaces all non-coin symbols with 0 (blank)
------------------------------------------------------- */
function transformForHoldAndWin(normalizedReels) {
  return normalizedReels.map((reel) => {
    if (!Array.isArray(reel)) return reel;
    return reel.map((symbolId) => {
      const n = typeof symbolId === "string" ? parseInt(symbolId, 10) : symbolId;
      return COIN_SYMBOL_IDS.includes(n) ? n : 0;
    });
  });
}

/* -------------------------------------------------------
   WORKER FACTORY AS BLOB
------------------------------------------------------- */
function createInlineWorker(code) {
  const blob = new Blob([code], { type: "application/javascript" });
  const url = URL.createObjectURL(blob);
  return new Worker(url);
}

/* -------------------------------------------------------
   INLINE spinWorker.js
   Same API contract as PopiPlay — CSRF auth, command-based payload
------------------------------------------------------- */
const spinWorkerCode = `
  importScripts("https://cdn.jsdelivr.net/npm/axios/dist/axios.min.js");

  onmessage = async (event) => {
    const { bet, token, apiUrl, csrfTokenName, csrfTokenValue, identifier, command = "spin", purchasedFeature, options } = event.data;

    if (!bet || !token || !apiUrl || !csrfTokenName || !csrfTokenValue) {
      postMessage({ error: "Dmon: Spin API call failed: Missing required parameters" });
      return;
    }

    try {
      const response = await axios.post(
        apiUrl,
        {
          command: command,
          options: options || {
            bet: bet,
            ...(purchasedFeature ? { purchased_feature: purchasedFeature } : {})
          }
        },
        {
          headers: {
            [csrfTokenName]: csrfTokenValue,
            "Content-Type": "application/json"
          }
        }
      );

      if (!response.data) {
        postMessage({ error: "Dmon: Spin API call failed: Invalid response structure" });
        return;
      }

      if (response.data.errors && Array.isArray(response.data.errors) && response.data.errors.length > 0) {
        postMessage({ error: response.data });
        return;
      }

      postMessage(response.data);

    } catch (error) {
      if (error.response?.data?.errors) {
        postMessage({ error: error.response.data });
      } else {
        postMessage({ error: "Dmon: Spin API call failed: " + error.message });
      }
    }
  };
`;

/* -------------------------------------------------------
   MAIN RGS PROVIDER
   PopiPlay API contract + noble-fruits game logic
------------------------------------------------------- */
class DmonRGS {
  constructor() {
    this.api_base_url = window.__OPTIONS__?.api || "";
    this.worker = null;
    this.currentToken = null;
  }

  // ============================================
  // REEL / SCREEN TRANSFORMS
  // ============================================

  /**
   * Normalize screen data — convert string symbol IDs to numbers.
   * API already returns exactly SYMBOLS_VISIBLE symbols per reel, no trimming needed.
   */
  transformReelsSymbols(response) {
    if (response.outcome && Array.isArray(response.outcome.screen)) {
      const normalized = normalizeScreenData(response.outcome.screen);
      response.outcome.reels = normalized;
      response.outcome.screen = normalized;
    }
    return response;
  }

  // ============================================
  // WIN CLASSIFICATION (from PixUpPlay)
  // ============================================

  /**
   * Compute win type from win amount and bet using provider thresholds
   */
  computeWinType(winAmount, betAmount) {
    if (!winAmount || winAmount <= 0 || !betAmount || betAmount <= 0) return "";

    const multiplier = winAmount / betAmount;
    const thresholds = PROVIDER_CONFIG.WIN_THRESHOLDS;
    const winTypes = PROVIDER_CONFIG.WIN_TYPES;

    const sorted = Object.entries(thresholds).sort(([, a], [, b]) => b - a);
    for (const [thresholdKey, thresholdValue] of sorted) {
      if (multiplier >= thresholdValue) {
        const winTypeKey = thresholdKey.replace("_THRESHOLD", "");
        return winTypes[winTypeKey] || "regular_win";
      }
    }
    return "regular_win";
  }

  // ============================================
  // EXPANDING WILD DETECTION (from PixUpPlay)
  // ============================================

  /**
   * Detect which middle reels (1, 2, 3) have wild symbols that are part of a connected winning line.
   */
  detectExpandingWilds(reels, wins) {
    if (!reels || !wins || !Array.isArray(wins) || wins.length === 0) return [];
    if (WILD_SYMBOL_ID === null) return [];

    const middleReelIndices = [1, 2, 3];
    const reelsWithWilds = [];

    for (const reelIndex of middleReelIndices) {
      const visibleSymbols = reels[reelIndex];
      if (!visibleSymbols || !Array.isArray(visibleSymbols)) continue;

      const wildPosition = visibleSymbols.findIndex(
        (symbolId) => symbolId === WILD_SYMBOL_ID
      );
      if (wildPosition === -1) continue;

      const isPartOfConnectedWin = wins.some((win) => {
        if (!Array.isArray(win.positions) || win.positions.length === 0) return false;
        const reelsInWin = win.positions.map(([r]) => r);
        const uniqueReels = [...new Set(reelsInWin)].sort((a, b) => a - b);
        if (!uniqueReels.includes(reelIndex)) return false;

        let rightmostConnected = -1;
        for (let i = 0; i < uniqueReels.length; i++) {
          const currentReel = uniqueReels[i];
          if (i === 0 && currentReel > 1) break;
          if (i > 0 && currentReel !== uniqueReels[i - 1] + 1) break;
          rightmostConnected = currentReel;
        }
        return rightmostConnected >= reelIndex;
      });

      if (isPartOfConnectedWin) {
        reelsWithWilds.push({ reelIndex, wildPosition });
      }
    }
    return reelsWithWilds;
  }

  // ============================================
  // WIN NORMALIZATION (PopiPlay tuple format)
  // ============================================

  /**
   * Normalize raw wins array from API (tuple format) into object format.
   * PopiPlay/Dmon API: [symbol, amount, positions, lineId]
   */
  normalizeWins(rawWins, screen) {
    if (!Array.isArray(rawWins)) return [];
    return rawWins
      .map((winTuple) => {
        if (!Array.isArray(winTuple) || winTuple.length < 4) return null;
        const [symbolOrType, amount, positions, lineIdOrCount] = winTuple;
        if (!Array.isArray(positions)) return null;

        // Detect scatter wins — first element is "scatter" string, not a symbol index
        const isScatter = symbolOrType === "scatter";

        const transformedPositions = positions.map((rowValue, reelIndex) => {
          if (Array.isArray(rowValue) && rowValue.length >= 2) {
            return [rowValue[0], rowValue[1]];
          }
          return [reelIndex, rowValue];
        });

        // For scatter wins, extract symbol from the screen at first position
        // For regular wins, extract from first position as well
        let symbol = null;
        if (Array.isArray(screen) && transformedPositions.length > 0) {
          const [reelIdx, rowIdx] = transformedPositions[0];
          if (screen[reelIdx]?.[rowIdx] !== undefined) {
            const sv = screen[reelIdx][rowIdx];
            const n = typeof sv === "string" ? parseInt(sv, 10) : sv;
            const map = PROVIDER_CONFIG.SYMBOL_MAP;
            symbol = map[n] !== undefined ? map[n] : n;
          }
        }

        return {
          id: isScatter ? null : lineIdOrCount,  // Scatter has no line ID (4th element is count)
          win: amount,
          positions: transformedPositions,
          symbol,
          isScatter,
        };
      })
      .filter((w) => w !== null);
  }

  // ============================================
  // ADAPT SPIN ROUND TO STATE
  // ============================================

  /**
   * Adapt spin data to universal state format.
   * Pre-computes win type, expanding wilds, scatter info.
   */
  adaptSpinRoundToState(normalizedOutcome, bet) {
    if (!normalizedOutcome) return null;

    const totalWin = normalizedOutcome.win || 0;
    const winType = this.computeWinType(totalWin, bet);
    const reels = normalizedOutcome.reels || [];
    const wins = normalizedOutcome.wins || [];

    const round = {
      reels,
      wins,
      win: totalWin,
      bet,
      winType,
      isAnimatedWin:
        winType !== "" && PROVIDER_CONFIG.ANIMATED_WIN_TYPES.includes(winType),
    };

    // Expanding wild detection
    if (FEATURES_CONFIG.EXPANDING_WILDS.ENABLED) {
      round.expandingWildReels = this.detectExpandingWilds(reels, wins);
      round.hasExpandingWilds = round.expandingWildReels.length > 0;
    }

    return round;
  }

  // ============================================
  // RESPONSE HANDLER
  // ============================================

  getResponseHandler() {
    return {
      patterns: {
        freeSpins: [
          {
            condition: () => false,
            extractFreeSpins: () => [],
            extractTriggerRound: (round) => round,
          },
        ],
        regular: {
          condition: (response) =>
            response.flow?.state === "ready" ||
            response.flow?.state === "closed" ||
            response.flow?.state === "freespins",
          extractRound: (outcome) => {
            if (!outcome) return {};

            const transformedOutcome = { ...outcome };

            if (Array.isArray(outcome.wins)) {
              transformedOutcome.wins = this.normalizeWins(outcome.wins, outcome.screen);
            } else {
              transformedOutcome.wins = [];
            }

            return transformedOutcome;
          },
        },
      },
      propertyMap: {
        balance: "balance.wallet",
        bet: "outcome.bet",
        win: "outcome.win",
        token: "flow.round_id",
        round: "outcome",
        reels: "outcome.screen",
        wins: "outcome.wins",
      },
    };
  }

  // ============================================
  // INIT GAME
  // ============================================

  async initGame(useApiData = false, ignoreFreeRoundsStorage = false) {
    if (!window.__OPTIONS__) {
      throw new Error("Dmon: window.__OPTIONS__ not found.");
    }

    const apiUrl = window.__OPTIONS__?.api;
    const playToken = window.__OPTIONS__?.play_token;
    const csrfHeaderName = window.__OPTIONS__?.csrfTokenHeaderName;
    const csrfHeaderValue = window.__OPTIONS__?.csrfTokenHeaderValue;
    const identifier = window.__OPTIONS__?.identifier;

    const missingFields = [];
    if (!apiUrl) missingFields.push("api");
    if (!playToken) missingFields.push("play_token");
    if (!csrfHeaderName) missingFields.push("csrfTokenHeaderName");
    if (!csrfHeaderValue) missingFields.push("csrfTokenHeaderValue");
    if (!identifier) missingFields.push("identifier");

    if (missingFields.length > 0) {
      throw new Error(
        `Dmon: Missing required fields in window.__OPTIONS__: [${missingFields.join(", ")}]`
      );
    }

    try {
      const response = await axios.post(
        apiUrl,
        { command: "init" },
        {
          headers: {
            [csrfHeaderName]: csrfHeaderValue,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.data) {
        throw new Error("Dmon: Invalid API response - response.data is missing");
      }

      if (
        response.data.errors &&
        Array.isArray(response.data.errors) &&
        response.data.errors.length > 0
      ) {
        const error = response.data.errors[0];
        throw new Error(`Dmon: API error: ${error.desc} (code: ${error.code})`);
      }

      const availableBets = response.data?.options?.available_bets;
      const defaultBet = response.data?.options?.default_bet;
      const baseBet = response.data?.options?.base_bet;
      const paytable = response.data?.options?.paytable;
      const lines = response.data?.options?.lines;
      const wallet = response.data?.balance?.wallet;
      const currencyObj = response.data?.options?.currency;
      const featureMultipliers =
        response.data?.options?.feature_options?.feature_multipliers;

      // Validate response structure
      if (!availableBets || !Array.isArray(availableBets)) {
        throw new Error("Dmon: Invalid API response - options.available_bets is missing or invalid");
      }
      if (typeof defaultBet !== "number") {
        throw new Error("Dmon: Invalid API response - options.default_bet is missing or invalid");
      }
      if (typeof baseBet !== "number") {
        throw new Error("Dmon: Invalid API response - options.base_bet is missing or invalid");
      }
      if (!paytable || typeof paytable !== "object") {
        throw new Error("Dmon: Invalid API response - options.paytable is missing or invalid");
      }
      if (!lines || !Array.isArray(lines)) {
        throw new Error("Dmon: Invalid API response - options.lines is missing or invalid");
      }
      if (typeof wallet !== "number") {
        throw new Error("Dmon: Invalid API response - balance.wallet is missing or invalid");
      }
      if (!currencyObj || typeof currencyObj.code !== "string") {
        throw new Error("Dmon: Invalid API response - options.currency.code is missing or invalid");
      }

      // Calculate bonus buy multiplier if present
      const bonusBuyMultiplier =
        featureMultipliers?.base_bet && featureMultipliers?.bonus_buy
          ? featureMultipliers.bonus_buy / featureMultipliers.base_bet
          : undefined;

      // Currency config for dynamic formatting
      const currencyConfig = {
        subunits: currencyObj.subunits || 100,
        exponent: currencyObj.exponent !== undefined ? currencyObj.exponent : 2,
        symbol: currencyObj.symbol || currencyObj.code || "",
      };

      // Session validation
      this.validateSession();

      // Detect feature state from API
      let featureState = this.detectFeatureState(response.data);

      // Load from storage
      const storedState = this.loadGameState(response.data, ignoreFreeRoundsStorage);

      // Free Rounds completion reload (rebuild from localStorage)
      if (
        storedState.reloadState?.needsReload &&
        storedState.reloadState.type === "freeRounds" &&
        storedState.reloadState.phase === "completion"
      ) {
        featureState = {
          type: "freeRounds",
          phase: "completion",
          data: {
            count: storedState.reloadState.data.count,
            performed: storedState.reloadState.data.performed,
            totalWin: storedState.reloadState.data.totalWin || 0,
            betValue: storedState.reloadState.data.betValue,
            betLevel: storedState.reloadState.data.betLevel,
          },
        };
      } else if (
        featureState.type === "freeRounds" &&
        storedState.reloadState?.needsReload &&
        storedState.reloadState.type === "freeRounds"
      ) {
        featureState.data.totalWin = storedState.reloadState.data.totalWin || 0;
      }

      // H&W intro/active — add transformed reels to featureState
      if (
        featureState.type === "holdAndWin" &&
        (featureState.phase === "intro" || featureState.phase === "active")
      ) {
        const outcome = response.data?.outcome;
        if (outcome?.screen && Array.isArray(outcome.screen)) {
          const normalizedScreen = normalizeScreenData(outcome.screen);

          if (featureState.phase === "intro") {
            const transformedReels = transformForHoldAndWin(normalizedScreen);
            featureState.data.transformedReels = transformedReels;
            featureState.data.currentRound = {
              reels: transformedReels,
              wins: this.normalizeWins(outcome.wins, outcome.screen),
              win: outcome.win || 0,
              bet: outcome.bet || storedState.bet || defaultBet,
              storage: outcome.storage || null,
            };
          } else if (featureState.phase === "active") {
            featureState.data.currentRound = {
              reels: normalizedScreen,
              wins: this.normalizeWins(outcome.wins, outcome.screen),
              win: outcome.win || 0,
              bet: outcome.bet || storedState.bet || defaultBet,
              storage: outcome.storage || null,
            };
          }
        }
      }

      // Merge API + Storage
      const balance = wallet;
      const savedBaseGame = StorageManager.loadBaseGameRound();
      const bet = storedState.bet || savedBaseGame?.bet || defaultBet;
      const round = useApiData
        ? this.extractRoundFromInit(response.data)
        : storedState.round || this.extractRoundFromInit(response.data);

      // Determine game mode
      const gameMode = this.getGameMode(featureState);

      // Free Rounds data extraction
      let freeRoundsData = storedState.freeRoundsData || null;
      if (!freeRoundsData) {
        const savedFreeRound =
          FEATURES_CONFIG.FREE_ROUNDS.ENABLED && !ignoreFreeRoundsStorage
            ? StorageManager.loadFreeRound()
            : null;

        if (savedFreeRound) {
          const apiTotalWin = response.data?.balance?.free_rounds ?? 0;
          const storageTotalWin = savedFreeRound.totalWin;
          const totalWin = Math.max(apiTotalWin, storageTotalWin);

          if (totalWin !== storageTotalWin) {
            StorageManager.saveFreeRound({
              round: savedFreeRound.round,
              bet: savedFreeRound.bet,
              balance: savedFreeRound.balance,
              performed: savedFreeRound.performed,
              count: savedFreeRound.count,
              totalWin: totalWin,
            });
          }

          freeRoundsData = {
            status: "active",
            count: savedFreeRound.count,
            performed: savedFreeRound.performed,
            betValue: savedFreeRound.bet,
            betLevel: null,
            totalWin: totalWin,
          };
        } else if (response.data?.free_rounds?.status === "active") {
          const freeRoundsTotalWin = response.data?.balance?.free_rounds || 0;
          freeRoundsData = {
            status: "active",
            count: response.data.free_rounds.count,
            performed: response.data.free_rounds.performed,
            betValue: response.data.free_rounds.bet_value,
            betLevel: response.data.free_rounds.bet_level,
            totalWin: freeRoundsTotalWin,
          };
        }
      }

      if (
        freeRoundsData &&
        featureState.type === "freeRounds" &&
        featureState.phase === "active" &&
        !featureState.data.totalWin
      ) {
        featureState.data.totalWin = freeRoundsData.totalWin;
      }

      const result = {
        balance,
        bet,
        baseBet,
        bets: availableBets,
        round,
        token: playToken,
        currency: currencyObj.code,
        currencyConfig,
        // Remap paytable keys from API symbol IDs to game symbol IDs
        paytable: Object.fromEntries(
          Object.entries(paytable).map(([apiId, values]) => [
            PROVIDER_CONFIG.SYMBOL_MAP[apiId] ?? apiId,
            values,
          ])
        ),
        winningLines: lines,
        bonusBuyMultiplier,
        game: identifier,
        gameMode,
        featureState,
        reloadState: storedState.reloadState,
        freeRoundsData,
        availableActions: response.data?.flow?.available_actions || ["init", "spin"],
        flowState: response.data?.flow?.state || "ready",
        features: response.data?.features || null,
        freeRounds: response.data?.free_rounds || null,
        command: "init",
      };

      if (response.data?.balance?.free_rounds !== undefined) {
        result.freeRoundsTotalWin = response.data.balance.free_rounds || 0;
      }

      // Persist init state on reload from API
      if (storedState.reloadState?.fromApi) {
        if (
          storedState.reloadState.type === "holdAndWin" &&
          FEATURES_CONFIG.HOLD_AND_WIN.ENABLED
        ) {
          StorageManager.saveBaseGameRound({ round: result.round, bet: result.bet, balance: result.balance });
          if (featureState.data?.currentRound) {
            const progressBar = storedState.reloadState.data?.progressBar ?? featureState.data.currentRound?.storage?.progress_bar ?? 0;
            const coinMultipliers = storedState.reloadState.data?.coinMultipliers ?? featureState.data.currentRound?.storage?.coin_multipliers ?? null;
            StorageManager.saveHoldAndWinRound({
              round: featureState.data.currentRound,
              respinsLeft: featureState.data.respinsLeft ?? 0,
              progressBar,
              coinMultipliers,
              bet: result.bet,
              balance: result.balance,
              introShown: false,
            });
          }
          if (response.data.free_rounds?.status === "active") {
            const freeRoundBet = response.data.free_rounds.bet_value ?? result.bet;
            const freeRoundsTotalWin = response.data.balance?.free_rounds ?? 0;
            StorageManager.saveFreeRound({
              round: result.round,
              bet: freeRoundBet,
              balance: result.balance,
              performed: response.data.free_rounds.performed ?? 0,
              count: response.data.free_rounds.count ?? 0,
              totalWin: freeRoundsTotalWin,
            });
          }
        }

        if (
          storedState.reloadState.type === "freeRounds" &&
          FEATURES_CONFIG.FREE_ROUNDS.ENABLED
        ) {
          const frData = storedState.reloadState.data;
          if (frData) {
            const freeRoundBet = frData.betValue ?? frData.bet;
            StorageManager.saveFreeRound({
              round: frData.round,
              bet: freeRoundBet,
              balance: frData.balance,
              performed: frData.performed ?? 0,
              count: frData.count ?? 0,
              totalWin: frData.totalWin ?? 0,
            });
            if (result.round) {
              StorageManager.saveBaseGameRound({ round: result.round, bet: result.bet, balance: result.balance });
            }
          }
        }
      }

      // Persist base game on first load
      if (
        result.gameMode === "base" &&
        result.round &&
        (!StorageManager.hasBaseGameRound() || useApiData)
      ) {
        StorageManager.saveBaseGameRound({ round: result.round, bet: result.bet, balance: result.balance });
      }

      return result;
    } catch (error) {
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const apiError = error.response.data.errors[0];
        throw new Error(`Dmon: API error: ${apiError.desc} (code: ${apiError.code})`);
      }
      if (error.message?.startsWith("Dmon:")) throw error;
      throw new Error(`Dmon: Init API call failed: ${error.message}`);
    }
  }

  // ============================================
  // SPIN
  // ============================================

  spin(bet, token, command, purchasedFeature, spinOptions) {
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        this.worker = createInlineWorker(spinWorkerCode);
      }

      const options = window.__OPTIONS__;
      if (!options) {
        reject(new Error("Dmon: window.__OPTIONS__ not found."));
        return;
      }

      const apiUrl = options.api;
      const csrfTokenHeaderName = options.csrfTokenHeaderName;
      const csrfTokenHeaderValue = options.csrfTokenHeaderValue;
      const identifier = options.identifier;
      const playToken = options.play_token;

      const missingFields = [];
      if (!apiUrl) missingFields.push("api");
      if (!csrfTokenHeaderName) missingFields.push("csrfTokenHeaderName");
      if (!csrfTokenHeaderValue) missingFields.push("csrfTokenHeaderValue");
      if (!identifier) missingFields.push("identifier");

      if (missingFields.length > 0) {
        reject(new Error(`Dmon: Missing required fields in window.__OPTIONS__: [${missingFields.join(", ")}]`));
        return;
      }

      this.worker.onmessage = (event) => {
        const data = event.data;

        if (data.error) {
          let errorCode = null;
          if (typeof data.error === "object" && data.error.errors && Array.isArray(data.error.errors)) {
            errorCode = data.error.errors[0]?.code;
          }

          // Normalize error code through provider config mapping (e.g. 1001 → 301)
          if (errorCode && PROVIDER_CONFIG.ERROR_CODES[errorCode] !== undefined) {
            errorCode = PROVIDER_CONFIG.ERROR_CODES[errorCode];
            // Also update the error object so downstream handlers see the normalized code
            if (data.error.errors && data.error.errors[0]) {
              data.error.errors[0].code = errorCode;
            }
          }

          let extractedBalance = null;
          if (typeof data.error === "object" && data.error.balance) {
            if (typeof data.error.balance === "object" && data.error.balance.wallet !== undefined) {
              extractedBalance = data.error.balance.wallet;
            } else if (typeof data.error.balance === "number") {
              extractedBalance = data.error.balance;
            }
          }

          if (extractedBalance !== null && typeof data.error === "object") {
            data.error.balance = extractedBalance;
          }

          if (typeof data.error === "object" && data.error.errors) {
            reject(data.error);
          } else {
            const normalizedError = {
              errors: [{ code: errorCode || 999, desc: typeof data.error === "string" ? data.error : "Unknown error" }],
            };
            if (extractedBalance !== null) normalizedError.balance = extractedBalance;
            reject(normalizedError);
          }
          return;
        }

        // Transform response
        const transformedData = this.transformReelsSymbols(data);

        // Token rotation
        if (transformedData.flow?.round_id) {
          this.currentToken = transformedData.flow.round_id;
        }

        // Extract balance.wallet
        let extractedBalance = transformedData.balance;
        if (transformedData.balance && typeof transformedData.balance === "object" && transformedData.balance.wallet !== undefined) {
          extractedBalance = transformedData.balance.wallet;
        }

        // Free rounds total win
        let freeRoundsTotalWin = null;
        if (transformedData.balance && typeof transformedData.balance === "object" && transformedData.balance.free_rounds !== undefined) {
          freeRoundsTotalWin = (transformedData.balance.free_rounds || 0) + (transformedData.balance.game || 0);
        }

        // Normalize outcome wins
        let normalizedOutcome = transformedData.outcome;
        if (transformedData.outcome) {
          const handler = this.getResponseHandler();
          normalizedOutcome = handler.patterns.regular.extractRound(transformedData.outcome);
        }

        // Add round_id for dedup
        if (normalizedOutcome && transformedData.flow?.round_id) {
          normalizedOutcome.id = transformedData.flow.round_id;
        }

        const isHoldAndWinTrigger =
          FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
          command === "spin" &&
          transformedData.flow?.state === "respin";
        const isRespin = command === "respin";

        // Detect feature state
        let featureState = this.detectFeatureState(transformedData);

        if (featureState.type === "freeRounds" && freeRoundsTotalWin !== null) {
          featureState.data.totalWin = freeRoundsTotalWin;
        }

        // H&W transformed reels
        if (featureState.type === "holdAndWin" && (isHoldAndWinTrigger || isRespin)) {
          const originalScreen = data.outcome?.screen;
          if (originalScreen && Array.isArray(originalScreen)) {
            const normalizedReels = normalizeScreenData(originalScreen);

            if (isHoldAndWinTrigger) {
              const transformedReels = transformForHoldAndWin(normalizedReels);
              featureState.data.transformedReels = transformedReels;
              featureState.data.currentRound = {
                reels: transformedReels,
                wins: normalizedOutcome.wins || [],
                win: normalizedOutcome.win || 0,
                bet: normalizedOutcome.bet,
                storage: transformedData.outcome?.storage || null,
              };
            } else if (isRespin) {
              featureState.data.currentRound = {
                reels: normalizedReels,
                wins: normalizedOutcome.wins || [],
                win: normalizedOutcome.win || 0,
                bet: normalizedOutcome.bet,
                storage: transformedData.outcome?.storage || null,
              };
            }
          }
        }

        const gameMode = this.getGameMode(featureState);

        // Calculate balance
        const winAmount = normalizedOutcome?.win || 0;
        let finalBalance;
        const savedFreeRound = StorageManager.loadFreeRound();
        const freeRoundsCleared = !savedFreeRound;

        if (featureState.type === "freeRounds" && featureState.phase === "active") {
          finalBalance = extractedBalance;
        } else if (featureState.type === "freeRounds" && featureState.phase === "completion" && freeRoundsCleared) {
          const betAmount = normalizedOutcome?.bet || 0;
          finalBalance = extractedBalance - betAmount + winAmount;
        } else {
          finalBalance = extractedBalance + winAmount;
        }

        // Adapt round with win classification + expanding wilds
        const adaptedRound = this.adaptSpinRoundToState(normalizedOutcome, bet);

        // Save to storage
        const gameState = {
          balance: finalBalance,
          bet: transformedData.outcome?.bet || null,
          round: adaptedRound || normalizedOutcome,
          gameMode,
          featureState,
        };
        this.saveGameState(gameState, transformedData);

        // Clear completion data on new base game spin
        if (command === "spin") {
          if (gameMode === "base") {
            this.clearCompletionData();
          } else if (featureState.type === "freeRounds") {
            this.clearHoldAndWinOnly();
          }
        }

        // Build result
        const result = {
          token: transformedData.flow?.round_id || null,
          bet: gameState.bet,
          balance: finalBalance,
          round: adaptedRound || normalizedOutcome,
          gameMode,
          featureState,
          availableActions: transformedData.flow?.available_actions || ["init", "spin"],
          flowState: transformedData.flow?.state || "ready",
          features: transformedData.features || null,
          command,
          freeRounds: transformedData.free_rounds || null,
          freeRoundsTotalWin,
        };

        if (isRespin) {
          const originalScreen = data.outcome?.screen;
          if (originalScreen && Array.isArray(originalScreen)) {
            const respinRound = { ...normalizedOutcome };
            respinRound.reels = normalizeScreenData(originalScreen);
            result.holdAndWinRound = respinRound;
          }
        } else if (isHoldAndWinTrigger) {
          result.round = adaptedRound || normalizedOutcome;
          const originalScreen = data.outcome?.screen;
          if (originalScreen && Array.isArray(originalScreen)) {
            const normalizedReels = normalizeScreenData(originalScreen);
            const transformedReels = transformForHoldAndWin(normalizedReels);
            result.holdAndWinInitialReels = transformedReels;
            const transformedRound = { ...normalizedOutcome };
            transformedRound.reels = transformedReels;
            result.holdAndWinRound = transformedRound;
          }
        } else {
          result.round = adaptedRound || normalizedOutcome;
        }

        resolve(result);
      };

      this.worker.onerror = (event) => {
        reject(new Error(`Dmon: Worker error: ${event.message || "Unknown worker error"}`));
      };

      const tokenToUse = token || this.currentToken || playToken;

      this.worker.postMessage({
        bet,
        token: tokenToUse,
        apiUrl,
        csrfTokenName: csrfTokenHeaderName,
        csrfTokenValue: csrfTokenHeaderValue,
        identifier,
        command,
        ...(purchasedFeature ? { purchasedFeature } : {}),
        ...(spinOptions ? { options: spinOptions } : {}),
      });
    });
  }

  // ============================================
  // SPIN PARAMS
  // ============================================

  getSpinParams({ bet, featureState, purchasedFeature }) {
    if (featureState?.type === "freeRounds") {
      return { command: "spin", spinOptions: { bonus: true, bet: featureState.data.betValue } };
    }
    if (featureState?.type === "holdAndWin") {
      return { command: "respin", spinOptions: undefined };
    }
    return { command: "spin", spinOptions: undefined };
  }

  // ============================================
  // SIMULATE (not implemented)
  // ============================================

  async simulate(slotface, bet, token) {
    throw new Error("simulate() must be implemented");
  }

  // ============================================
  // BALANCE STRATEGY
  // ============================================

  getBalanceStrategy() {
    return {
      serverIncludesWin: false,
      clientDeductsBet: true,
      clientAddsWin: true,
    };
  }

  shouldSyncBalanceOnWinClose() {
    return true;
  }

  // ============================================
  // SESSION MANAGEMENT
  // ============================================

  validateSession() {
    const currentSession = window.__OPTIONS__?.cache_player_id;
    const storedSession = StorageManager.getStoredSessionToken();

    if (!currentSession) return false;

    if (currentSession !== storedSession) {
      StorageManager.clearSessionData();
      StorageManager.saveSessionToken(currentSession);
      return false;
    }
    return true;
  }

  getCurrentToken() {
    return this.currentToken || null;
  }



  // ============================================
  // FEATURE DETECTION (same logic as PopiPlay)
  // ============================================

  detectFeatureState(apiResponse) {
    const { flow, features, free_rounds } = apiResponse;

    if (
      FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
      features?.respins_left !== undefined &&
      (flow?.state === "respin" || features.trigger_screen !== undefined)
    ) {
      const phase = this.detectHoldAndWinPhase(features, apiResponse.outcome);
      return {
        type: "holdAndWin",
        phase,
        data: {
          respinsLeft: features.respins_left,
          triggerScreen: features.trigger_screen,
          progressBar: apiResponse.outcome?.storage?.progress_bar || 0,
          coinMultipliers: apiResponse.outcome?.storage?.coin_multipliers || null,
        },
      };
    }

    if (FEATURES_CONFIG.FREE_ROUNDS.ENABLED && free_rounds?.status === "active") {
      return {
        type: "freeRounds",
        phase: "active",
        data: {
          count: free_rounds.count,
          performed: free_rounds.performed,
          betValue: free_rounds.bet_value,
          betLevel: free_rounds.bet_level,
          totalWin: 0,
        },
      };
    }

    if (FEATURES_CONFIG.FREE_ROUNDS.ENABLED && free_rounds?.status === "played") {
      return {
        type: "freeRounds",
        phase: "completion",
        data: {
          count: free_rounds.count,
          performed: free_rounds.performed,
          totalWin: apiResponse?.balance?.free_rounds ?? 0,
        },
      };
    }

    return { type: null, phase: null, data: null };
  }

  detectHoldAndWinPhase(features, outcome) {
    if (hasRegularSymbols(outcome?.screen)) return "intro";
    return "active";
  }

  detectHoldAndWinFromApi(apiResponse) {
    const { flow, features, outcome } = apiResponse;
    if (
      !FEATURES_CONFIG.HOLD_AND_WIN.ENABLED ||
      features?.respins_left === undefined ||
      (flow?.state !== "respin" && features?.trigger_screen === undefined)
    ) {
      return null;
    }
    const phase = this.detectHoldAndWinPhase(features, outcome);
    return {
      phase,
      data: {
        respinsLeft: features.respins_left,
        triggerScreen: features.trigger_screen,
        progressBar: outcome?.storage?.progress_bar || 0,
        coinMultipliers: outcome?.storage?.coin_multipliers || null,
      },
    };
  }

  detectFreeRoundsFromApi(apiResponse) {
    const { free_rounds } = apiResponse;
    if (!FEATURES_CONFIG.FREE_ROUNDS.ENABLED || !free_rounds?.status) return null;

    if (free_rounds.status === "active") {
      return {
        phase: "active",
        count: free_rounds.count,
        performed: free_rounds.performed,
        betValue: free_rounds.bet_value,
        betLevel: free_rounds.bet_level,
      };
    }
    if (free_rounds.status === "played") {
      return {
        phase: "completion",
        count: free_rounds.count,
        performed: free_rounds.performed,
      };
    }
    return null;
  }

  getGameMode(featureState) {
    if (featureState.type && featureState.phase === "intro") return "base";
    return featureState.type ? "feature" : "base";
  }

  // ============================================
  // STORAGE MANAGEMENT (same as PopiPlay)
  // ============================================

  saveGameState(gameState, apiResponse) {
    const { gameMode, featureState, round, bet, balance } = gameState;
    const { free_rounds } = apiResponse;

    if (gameMode === "base") {
      StorageManager.saveBaseGameRound({ round, bet, balance });
    } else if (featureState.type === "freeRounds") {
      const freeRoundBet = featureState.data.betValue ?? bet;
      const existingFreeRounds = StorageManager.loadFreeRound();
      const shouldSkipSave = featureState.data.performed === 0 && existingFreeRounds;

      if (!shouldSkipSave) {
        if (featureState.data.performed === 0) {
          const savedBaseGame = StorageManager.loadBaseGameRound();
          const baseGameBetToPreserve = savedBaseGame?.bet ?? bet;
          StorageManager.saveFreeRound({
            round, bet: freeRoundBet, balance,
            performed: featureState.data.performed,
            count: featureState.data.count,
            totalWin: featureState.data.totalWin || 0,
            betToRestore: baseGameBetToPreserve,
          });
        } else {
          StorageManager.saveFreeRound({
            round, bet: freeRoundBet, balance,
            performed: featureState.data.performed,
            count: featureState.data.count,
            totalWin: featureState.data.totalWin || 0,
          });
        }
      }

      if (featureState.phase === "active") {
        const savedBaseGame = StorageManager.loadBaseGameRound();
        const baseGameBetToKeep = savedBaseGame?.bet ?? bet;
        StorageManager.saveBaseGameRound({ round, bet: baseGameBetToKeep, balance });
      }
    } else if (featureState.type === "holdAndWin") {
      if (featureState.phase === "intro") {
        StorageManager.saveBaseGameRound({ round, bet, balance });
        StorageManager.saveHoldAndWinRound({
          round: featureState.data.currentRound,
          respinsLeft: featureState.data.respinsLeft,
          progressBar: featureState.data.progressBar || 0,
          coinMultipliers: featureState.data.coinMultipliers || null,
          bet, balance, introShown: false,
        });
        if (free_rounds?.status === "active" || free_rounds?.status === "played") {
          const freeRoundBet = free_rounds.bet_value ?? bet;
          const existingFreeRound = StorageManager.loadFreeRound();
          const roundToSave = existingFreeRound?.round || round;
          const accumulatedWin = apiResponse.balance?.free_rounds ?? 0;
          const triggerRoundWin = apiResponse.balance?.game ?? 0;
          StorageManager.saveFreeRound({
            round: roundToSave, bet: freeRoundBet, balance,
            performed: free_rounds.performed,
            count: free_rounds.count,
            totalWin: accumulatedWin + triggerRoundWin,
          });
        }
      } else if (featureState.phase === "active") {
        const isFinalRound = featureState.data.respinsLeft === 0;
        const maxWinThreshold = bet * FEATURES_CONFIG.HOLD_AND_WIN.MAX_WIN_MULTIPLIER;
        const totalWin = featureState.data.currentRound?.win || 0;
        const isMaxWin = totalWin >= maxWinThreshold;

        StorageManager.saveHoldAndWinRound({
          round: featureState.data.currentRound,
          respinsLeft: featureState.data.respinsLeft,
          progressBar: featureState.data.progressBar || 0,
          coinMultipliers: featureState.data.coinMultipliers || null,
          bet, balance, introShown: true,
          ...(isFinalRound && { totalWin, isMaxWin }),
        });

        if (free_rounds?.status === "active" || free_rounds?.status === "played") {
          const savedFreeRound = StorageManager.loadFreeRound();
          const freeRoundBet = free_rounds.bet_value ?? bet;

          if (isFinalRound) {
            const accumulatedWin = apiResponse.balance?.free_rounds ?? 0;
            const currentGameWin = apiResponse.balance?.game ?? 0;
            const existingRound = savedFreeRound?.round || round;
            StorageManager.saveFreeRound({
              round: existingRound, bet: freeRoundBet, balance,
              performed: free_rounds.performed,
              count: free_rounds.count,
              totalWin: accumulatedWin + currentGameWin,
            });
          } else if (savedFreeRound) {
            StorageManager.saveFreeRound({
              round: savedFreeRound.round, bet: freeRoundBet, balance,
              performed: free_rounds.performed,
              count: free_rounds.count,
              totalWin: savedFreeRound.totalWin,
            });
          }
        }
      }
    }
  }

  // ============================================
  // LOAD GAME STATE (same as PopiPlay)
  // ============================================

  collectFeatureStates(apiResponse, ignoreFreeRoundsStorage = false) {
    return {
      holdAndWin: {
        storage: FEATURES_CONFIG.HOLD_AND_WIN.ENABLED ? StorageManager.loadHoldAndWinRound() : null,
        api: this.detectHoldAndWinFromApi(apiResponse),
      },
      freeRounds: {
        storage: FEATURES_CONFIG.FREE_ROUNDS.ENABLED && !ignoreFreeRoundsStorage ? StorageManager.loadFreeRound() : null,
        api: this.detectFreeRoundsFromApi(apiResponse),
      },
    };
  }

  determineFeaturePriority(features) {
    const hnw = features.holdAndWin;
    const fr = features.freeRounds;

    if (hnw.storage || hnw.api) {
      return { primary: "holdAndWin", secondary: fr.storage || fr.api ? "freeRounds" : null };
    }
    if (fr.storage || fr.api) {
      return { primary: "freeRounds", secondary: null };
    }
    return { primary: "base", secondary: null };
  }

  buildHoldAndWinReload(features, priority, apiResponse) {
    const hnw = features.holdAndWin;
    const fr = features.freeRounds;
    const { outcome, options, free_rounds } = apiResponse;

    let hnwPhase;
    let hnwData;

    if (hnw.storage) {
      hnwData = hnw.storage;
      if (hnwData.respinsLeft === 0) hnwPhase = "completion";
      else if (hnwData.introShown === false) hnwPhase = "intro";
      else hnwPhase = "active";
    } else {
      hnwPhase = hnw.api.phase;
      hnwData = hnw.api.data;
    }

    const savedBaseGame = StorageManager.loadBaseGameRound();
    const triggerRound = savedBaseGame?.round || this.extractTriggerRoundFromInit(apiResponse);
    const bet = hnw.storage ? hnw.storage.bet : (outcome?.bet ?? options?.default_bet ?? null);
    const hasApiData = hnw.api || (priority.secondary === "freeRounds" && fr.api);

    const result = {
      reloadState: {
        needsReload: true,
        type: "holdAndWin",
        phase: hnwPhase,
        data: hnwData,
        ...(hasApiData && { fromApi: true }),
      },
      bet,
      round: triggerRound,
    };

    if (priority.secondary === "freeRounds") {
      const freeRoundBet = fr.storage ? fr.storage.bet : (free_rounds?.bet_value ?? outcome?.bet ?? options?.default_bet ?? null);
      const freeRoundsTotalWin = fr.storage ? fr.storage.totalWin : (apiResponse?.balance?.free_rounds ?? 0);
      result.freeRoundsData = {
        status: "active",
        count: fr.storage ? fr.storage.count : fr.api.count,
        performed: fr.storage ? fr.storage.performed : fr.api.performed,
        betValue: freeRoundBet,
        betLevel: fr.storage ? null : (fr.api?.betLevel ?? null),
        totalWin: freeRoundsTotalWin,
      };
    }
    return result;
  }

  buildFreeRoundsReload(features, apiResponse) {
    const fr = features.freeRounds;
    const { outcome, options, free_rounds } = apiResponse;

    let phase;
    let frData;

    if (fr.storage) {
      phase = fr.storage.performed === fr.storage.count ? "completion" : "active";
      frData = fr.storage;
    } else {
      phase = fr.api.phase;
      const savedBaseGame = StorageManager.loadBaseGameRound();
      const freeRoundBet = free_rounds?.bet_value ?? outcome?.bet ?? options?.default_bet ?? null;
      const round = savedBaseGame?.round ?? (outcome?.screen ? {
        reels: normalizeScreenData(outcome.screen),
        wins: outcome.wins || [],
        win: outcome.win || 0,
      } : null);

      frData = {
        round, bet: freeRoundBet,
        balance: apiResponse?.balance?.wallet,
        performed: fr.api.performed,
        count: fr.api.count,
        totalWin: apiResponse?.balance?.free_rounds ?? 0,
        betValue: freeRoundBet,
        betLevel: fr.api.betLevel,
      };
    }

    let betToUse;
    if (phase === "completion") {
      const savedBaseGame = StorageManager.loadBaseGameRound();
      const betToRestore = fr.storage?.betToRestore ?? savedBaseGame?.bet;
      betToUse = betToRestore || frData.bet;
    } else {
      betToUse = frData.bet;
    }

    return {
      round: frData.round,
      bet: betToUse,
      reloadState: {
        needsReload: true,
        type: "freeRounds",
        phase,
        ...(fr.api && { fromApi: true }),
        data: {
          round: frData.round, bet: frData.bet, balance: frData.balance,
          performed: frData.performed, count: frData.count,
          totalWin: frData.totalWin ?? 0,
          betValue: frData.betValue ?? frData.bet,
          betLevel: frData.betLevel ?? null,
        },
      },
    };
  }

  buildBaseGameReload() {
    const savedBaseGame = StorageManager.loadBaseGameRound();
    if (savedBaseGame) {
      return { round: savedBaseGame.round, bet: savedBaseGame.bet, reloadState: { needsReload: false } };
    }
    return { reloadState: { needsReload: false } };
  }

  loadGameState(apiResponse, ignoreFreeRoundsStorage = false) {
    const features = this.collectFeatureStates(apiResponse, ignoreFreeRoundsStorage);
    const priority = this.determineFeaturePriority(features);

    if (priority.primary === "holdAndWin") {
      return this.buildHoldAndWinReload(features, priority, apiResponse);
    }
    if (priority.primary === "freeRounds") {
      if (features.freeRounds.api?.phase === "completion" && !features.freeRounds.storage) {
        return this.buildBaseGameReload();
      }
      return this.buildFreeRoundsReload(features, apiResponse);
    }
    return this.buildBaseGameReload();
  }

  clearCompletionData() {
    if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && StorageManager.hasHoldAndWinRound()) {
      StorageManager.clearHoldAndWinRound();
    }
    if (FEATURES_CONFIG.FREE_ROUNDS.ENABLED && StorageManager.hasFreeRound()) {
      StorageManager.clearFreeRound();
    }
  }

  clearHoldAndWinOnly() {
    if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && StorageManager.hasHoldAndWinRound()) {
      StorageManager.clearHoldAndWinRound();
    }
  }

  // ============================================
  // ROUND EXTRACTION HELPERS
  // ============================================

  extractRoundFromInit(apiResponse) {
    const screen = apiResponse?.options?.screen;
    if (screen && Array.isArray(screen)) {
      return { reels: normalizeScreenData(screen), wins: [], win: 0 };
    }
    return null;
  }

  extractTriggerRoundFromInit(apiResponse) {
    const triggerScreen = apiResponse?.features?.trigger_screen;
    if (triggerScreen && Array.isArray(triggerScreen)) {
      return {
        reels: normalizeScreenData(triggerScreen),
        wins: this.normalizeWins(apiResponse?.outcome?.wins, apiResponse?.outcome?.screen),
        win: apiResponse?.outcome?.win || 0,
      };
    }
    return this.extractRoundFromInit(apiResponse);
  }

  extractFreeRoundsTotalWin(apiResponse) {
    return (apiResponse.balance?.free_rounds || 0) + (apiResponse.balance?.game || 0);
  }
}

export { spinWorkerCode };

export default DmonRGS;
